/** チラシ・公式文面などから予定フォーム用の値を推測（必ず人間が確認すること） */

export type ParsedFromText = {
  /** フォームの Row にマージするフィールド（空は送らない） */
  patch: Record<string, string | number>;
  /** 出演者欄に追加する名前 */
  perfNames: string[];
};

function pad2(n: string): string {
  return n.length === 1 ? `0${n}` : n;
}

/** 和暦・スラッシュは未対応。YYYY-MM-DD / YYYY/MM/DD / YYYY年M月D日 */
export function extractIsoDates(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (y: string, mo: string, d: string) => {
    const iso = `${y}-${pad2(mo)}-${pad2(d)}`;
    if (!seen.has(iso)) {
      seen.add(iso);
      out.push(iso);
    }
  };
  for (const m of text.matchAll(/(\d{4})-(\d{2})-(\d{2})/g)) add(m[1]!, m[2]!, m[3]!);
  for (const m of text.matchAll(/(\d{4})[\/\.年](\d{1,2})[\/\.月](\d{1,2})日?/g)) add(m[1]!, m[2]!, m[3]!);
  return out;
}

function extractUrls(text: string): string[] {
  const raw = [...text.matchAll(/https?:\/\/[^\s\]<〉）】\],]+/gi)].map((x) => x[0].replace(/[、。,.]+$/, ""));
  const seen = new Set<string>();
  return raw.filter((u) => {
    if (seen.has(u)) return false;
    seen.add(u);
    return true;
  });
}

/** 「開場12:30 開演13:00」形式を行ごと・最大3組 */
function extractDoorCurtainPairs(text: string): { door: string; curtain: string }[] {
  const pairs: { door: string; curtain: string }[] = [];
  const lines = text.split(/\n/);
  const lineRe = /開場\D{0,12}(\d{1,2}:\d{2})\D{0,24}開演\D{0,12}(\d{1,2}:\d{2})/;
  for (const line of lines) {
    const m = line.match(lineRe);
    if (m) pairs.push({ door: m[1]!, curtain: m[2]! });
  }
  if (pairs.length === 0) {
    const g = text.match(/開場\D{0,12}(\d{1,2}:\d{2})\D{0,40}開演\D{0,12}(\d{1,2}:\d{2})/);
    if (g) pairs.push({ door: g[1]!, curtain: g[2]! });
  }
  return pairs.slice(0, 3);
}

function guessEventName(text: string): string | null {
  const lines = text
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  for (const l of lines) {
    if (/^https?:\/\//i.test(l)) continue;
    if (/^(開場|開演|会場|日程|受付|チケット|URL)/.test(l)) continue;
    if (l.includes("【") && l.includes("】") && l.length <= 200) return l;
  }
  for (const l of lines) {
    if (/^https?:\/\//i.test(l)) continue;
    if (l.length >= 4 && l.length <= 120 && !/^\d{4}[-\/年]/.test(l)) return l;
  }
  return null;
}

function guessVenue(text: string): string | null {
  const m =
    text.match(/会場[：:\s]*([^\n\r]+)/) ||
    text.match(/開催場所[：:\s]*([^\n\r]+)/) ||
    text.match(/[＠@]\s*([^\n\r]+)/);
  return m ? m[1]!.trim() : null;
}

const PREFS = "北海道|青森県|岩手県|宮城県|秋田県|山形県|福島県|茨城県|栃木県|群馬県|埼玉県|千葉県|東京都|神奈川県|新潟県|富山県|石川県|福井県|山梨県|長野県|岐阜県|静岡県|愛知県|三重県|滋賀県|京都府|大阪府|兵庫県|奈良県|和歌山県|鳥取県|島根県|岡山県|広島県|山口県|徳島県|香川県|愛媛県|高知県|福岡県|佐賀県|長崎県|熊本県|大分県|宮崎県|鹿児島県|沖縄県";

function guessPrefecture(text: string): string | null {
  const re = new RegExp(`(${PREFS})`);
  const m = text.match(re);
  return m ? m[1]! : null;
}

function guessPerfNames(text: string): string[] {
  const names = new Set<string>();
  if (/宮本\s*佳林|佳林ちゃん|宮本佳林/.test(text)) names.add("宮本佳林");
  const known = ["モーニング娘。", "アンジュルム", "Juice=Juice", "つばきファクトリー", "BEYOOOOONDS", "OCHA NORMA"];
  for (const k of known) {
    if (text.includes(k)) names.add(k);
  }
  return [...names];
}

function guessEventKind(text: string): string | null {
  if (/配信|オンライン|YouTube|ニコ生/.test(text)) return "stream";
  if (/ラジオ/.test(text)) return "radio";
  if (/テレビ|TV/.test(text)) return "tv";
  if (/ミニライブ|お見送り|お話し会/.test(text)) return "event";
  if (/サイン会|リミスタ|インターネットサイン/.test(text)) return "sign";
  if (/ミュージカル/.test(text)) return "musical";
  if (/舞台(?!ミュージカル)/.test(text)) return "stage";
  if (/コンサート|\bLIVE\b|ライブ(?!ミニ)/.test(text)) return "concert";
  return null;
}

function guessKarinSubtype(text: string): string | null {
  if (/リミスタ|インターネットサイン/.test(text)) return "limista";
  if (/WithLIVE|ウィズライブ/.test(text)) return "withlive";
  if (/ミニライブ|お見送り|一言/.test(text)) return "mini_live";
  return null;
}

function guessReceptionIsoRange(text: string): { start: string | null; end: string | null } {
  const dates = extractIsoDates(text);
  if (dates.length >= 2 && /受付|発売|チケット|先行|一般/.test(text)) {
    const sorted = [...dates].sort();
    return { start: sorted[0]!, end: sorted[sorted.length - 1]! };
  }
  return { start: null, end: null };
}

/**
 * 日本語中心の貼り付けテキストから、フォームにマージできる値を推測する。
 */
export function parseEventFieldsFromText(raw: string): ParsedFromText {
  const text = raw.normalize("NFKC").trim();
  const patch: Record<string, string | number> = {};
  const perfNames = guessPerfNames(text);

  const urls = extractUrls(text);
  if (urls[0]) patch.detail_url = urls[0]!;
  if (urls[1]) patch.reserve_url = urls[1]!;

  const dates = extractIsoDates(text);
  if (dates[0]) patch.event_date = dates[0]!;
  if (dates.length >= 2) patch.homepage_public_date = dates[1]!;
  else if (dates[0] && /掲載開始|掲載期間|一覧掲載|ホームページ公開/.test(text)) patch.homepage_public_date = dates[0]!;

  const name = guessEventName(text);
  if (name) patch.event_name = name;

  const venue = guessVenue(text);
  if (venue) patch.venue = venue;

  const pref = guessPrefecture(text);
  if (pref) patch.prefecture = pref;

  const pairs = extractDoorCurtainPairs(text);
  if (pairs[0]) {
    patch.door_time = pairs[0]!.door;
    patch.curtain_time = pairs[0]!.curtain;
  }
  if (pairs[1]) {
    patch.door_time_2 = pairs[1]!.door;
    patch.curtain_time_2 = pairs[1]!.curtain;
  }
  if (pairs[2]) {
    patch.door_time_3 = pairs[2]!.door;
    patch.curtain_time_3 = pairs[2]!.curtain;
  }

  const timeRange = text.match(/(\d{1,2}:\d{2})\s*[〜～-]\s*(\d{1,2}:\d{2})/);
  if (timeRange && !patch.time_start) {
    patch.time_start = timeRange[1]!;
    patch.time_end = timeRange[2]!;
  }

  const { start, end } = guessReceptionIsoRange(text);
  if (start) patch.reception_start = start;
  if (end) patch.reception_end = end;

  const ek = guessEventKind(text);
  if (ek) patch.event_kind = ek;

  const ks = guessKarinSubtype(text);
  if (ks) patch.karin_subtype = ks;

  if (perfNames.includes("宮本佳林")) patch.is_karin = 1;

  return { patch, perfNames };
}
